import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Mon volet roulant électrique ne répond plus à la télécommande, que faire ?', a: 'Vérifiez d\'abord les piles de la télécommande. Si le problème persiste, il peut s\'agir d\'une désynchronisation entre la télécommande et le moteur, ou d\'un récepteur radio défectueux. Nos techniciens peuvent reprogrammer votre télécommande ou remplacer le récepteur si nécessaire.' },
  { q: 'Pourquoi mon volet électrique s\'arrête-t-il en cours de course ?', a: 'C\'est généralement un problème de fin de course : le moteur « croit » que le volet est arrivé en butée et s\'arrête. Un simple réglage des fins de course suffit souvent à résoudre le problème. Dans certains cas, un obstacle dans les rails ou un condensateur fatigué peuvent aussi être en cause.' },
];

export default function VoletElectrique() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Volet roulant électrique à Lyon : dépannage & pose | DFPRO"
        description="Panne de volet roulant électrique à Lyon ? Diagnostic moteur, condensateur, télécommande, fin de course. Intervention 7j/7, devis gratuit. 09 80 80 36 84."
        canonicalUrl="https://depanfenetres-lyon.fr/volet-roulant-electrique-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-electrique-2026.jpg" alt="Dépannage de volet roulant électrique à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Volet électrique</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Volet roulant électrique à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Votre volet roulant électrique est en panne ? Télécommande qui ne répond plus, moteur HS, fin de course déréglé ? Dépannage rapide à Lyon et dans la métropole.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Comprendre les pannes du volet roulant électrique</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le volet roulant électrique apporte un confort indéniable au quotidien, mais sa technologie peut parfois montrer des signes de faiblesse. Contrairement à un volet manuel dont la panne est souvent mécanique et visible, les pannes électriques demandent un diagnostic plus poussé pour identifier précisément le composant défectueux.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Les pannes les plus courantes concernent le moteur (usure, surchauffe, défaut de fabrication), le condensateur (petit composant essentiel au démarrage du moteur), la télécommande (désynchronisation, pile HS, récepteur radio défectueux), et le fin de course (mécanisme qui indique au moteur quand s'arrêter).
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Nos techniciens disposent de l'outillage et des connaissances nécessaires pour diagnostiquer rapidement l'origine de la panne. Nous intervenons avec un stock de pièces détachées (condensateurs, télécommandes, moteurs) pour pouvoir réparer dans la foulée, sans avoir à commander et revenir.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Nos interventions sur volet roulant électrique</h2>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { title: 'Remplacement moteur', desc: 'Moteur filaire ou radio, toutes marques.' },
                      { title: 'Changement condensateur', desc: 'Réparation économique sans changer le moteur.' },
                      { title: 'Réglage fin de course', desc: 'Pour que votre volet s\'arrête à la bonne position.' },
                      { title: 'Programmation télécommande', desc: 'Resynchronisation ou remplacement.' },
                      { title: 'Remplacement interrupteur', desc: 'Interrupteur mural défectueux ou obsolète.' },
                      { title: 'Câblage électrique', desc: 'Vérification et réparation du circuit électrique.' },
                    ].map((item, idx) => (
                      <div key={idx} className="bg-background-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                          <h3 className="font-heading font-semibold text-sm text-foreground-800">{item.title}</h3>
                        </div>
                        <p className="text-xs text-secondary-500 ml-6">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/reparation-moteur-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation moteur</Link>
                    <Link to="/motorisation-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Motorisation</Link>
                    <Link to="/volet-roulant-solaire-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet solaire</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — volet roulant électrique</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Volet roulant électrique en panne à Lyon ?" subtitle="Diagnostic et réparation rapides. Toutes marques, 7j/7." />
      </main>
      <Footer />
    </>
  );
}